import { logReportDownload } from '../../util/auditLogger';
import * as xlsx from "xlsx";
import { thaiDate } from "../../util";

/**
 * ดาวน์โหลดรายงาน Excel (.xlsx) สำหรับ Tracking Link แต่ละรายการ
 * ประกอบด้วย 3 Sheets:
 * 1. สรุปภาพรวม (Summary)
 * 2. คำสั่งซื้อ 100 ล่าสุด (Latest 100 Orders)
 * 3. ประวัติกิจกรรม 100 ล่าสุด (Latest 100 Activity Logs)
 */
export const exportTrackingLinkToXLSX = ({
  linkInfo,
  code,
  selectedRound,
  displayStats = {},
  ordersList = [],
  events = [],
}) => {
  const wb = xlsx.utils.book_new();

  // จัดเรียงข้อมูลคำสั่งซื้อและประวัติกิจกรรมจากล่าสุดไปเก่าสุด และจำกัด 100 รายการล่าสุด
  const latestOrders = [...ordersList]
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, 100);

  const latestEvents = [...events]
    .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0))
    .slice(0, 100);

  // 1. Sheet สรุปภาพรวม (Summary)
  const summaryData = [
    ["รายงาน Tracking Link", linkInfo?.name || code],
    ["Tracking Code", code],
    ["ผู้ติดต่อ / อ้างอิง", linkInfo?.contact || "-"],
    ["หมายเหตุ", linkInfo?.note || "-"],
    ["งวดที่เลือก", selectedRound || "ทั้งหมด"],
    ["วันที่ออกรายงาน", new Date().toLocaleString("th-TH")],
    [],
    ["หัวข้อสถิติ", "จำนวน / ยอดเงิน", "หน่วย"],
    ["จำนวนผู้เข้าชม (Visits)", displayStats.visits || 0, "ครั้ง/Session"],
    ["จำนวนผู้สมัครสมาชิก (Registers)", displayStats.registers || 0, "คน"],
    ["เพิ่มสินค้าลงตะกร้า (Add to Cart)", displayStats.addToCarts || 0, "ครั้ง"],
    ["เริ่มกระบวนการชำระเงิน (Checkout)", displayStats.checkouts || 0, "ครั้ง"],
    ["แนบสลิป/ชำระเงิน (Payment)", displayStats.payments || 0, "ครั้ง"],
    ["คำสั่งซื้อทั้งหมดที่สร้าง (Total Orders)", displayStats.orderSuccesses || 0, "ออเดอร์"],
    ["คำสั่งซื้อที่อนุมัติแล้ว (Approved Orders)", displayStats.orderApproved || 0, "ออเดอร์"],
    ["คำสั่งซื้อที่ยกเลิก (Cancelled Orders)", displayStats.orderCancelled || 0, "ออเดอร์"],
    ["อัตราการแปลงผล (Conversion Rate)", `${displayStats.conversionRate || 0}%`, "%"],
    ["ยอดขายรวมก่อนหักยกเลิก (Gross Revenue)", displayStats.grossRevenue || 0, "บาท"],
    ["ยอดเงินที่ยกเลิก (Cancelled Revenue)", displayStats.cancelledRevenue || 0, "บาท"],
    ["ยอดขายสุทธิ (Net Revenue)", displayStats.totalRevenue || 0, "บาท"],
    [],
    ["ข้อมูลในรายงานนี้"],
    ["- คำสั่งซื้อ 100 ล่าสุด", latestOrders.length, "รายการ"],
    ["- ประวัติกิจกรรม 100 ล่าสุด", latestEvents.length, "รายการ"],
  ];
  const wsSummary = xlsx.utils.aoa_to_sheet(summaryData);
  wsSummary["!cols"] = [{ wch: 40 }, { wch: 30 }, { wch: 15 }];
  xlsx.utils.book_append_sheet(wb, wsSummary, "สรุปภาพรวม");

  // 2. Sheet คำสั่งซื้อ 100 ล่าสุด (Orders)
  const ordersHeaders = [
    "ลำดับ",
    "วันที่-เวลา",
    "Order ID",
    "Order Short ID",
    "ชื่อผู้ซื้อ",
    "เบอร์โทรศัพท์",
    "ประเภทผู้ซื้อ",
    "User ID",
    "ยอดเงิน (บาท)",
    "จำนวนสลาก (ใบ)",
    "สถานะคำสั่งซื้อ",
  ];

  const ordersRows = latestOrders.map((ord, idx) => [
    idx + 1,
    ord.createdAt ? new Date(ord.createdAt).toLocaleString("th-TH") : "-",
    ord.orderId || "-",
    ord.orderShortId ? `#${ord.orderShortId}` : "-",
    ord.customerName || (ord.isGuest ? "ลูกค้าทั่วไป (Guest)" : "สมาชิก"),
    ord.customerPhone || "-",
    ord.isGuest ? "Guest" : "Member",
    ord.userId || "-",
    ord.orderAmount || 0,
    ord.orderSize || 0,
    ord.status === "approved"
      ? "อนุมัติแล้ว"
      : ord.status === "cancelled"
      ? "ยกเลิกแล้ว"
      : "รอตรวจสลิป",
  ]);

  const wsOrders = xlsx.utils.aoa_to_sheet([ordersHeaders, ...ordersRows]);
  wsOrders["!cols"] = [
    { wch: 8 },
    { wch: 20 },
    { wch: 28 },
    { wch: 16 },
    { wch: 24 },
    { wch: 16 },
    { wch: 14 },
    { wch: 28 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
  ];
  xlsx.utils.book_append_sheet(wb, wsOrders, "คำสั่งซื้อ 100 ล่าสุด");

  // 3. Sheet ประวัติกิจกรรม 100 ล่าสุด (Activity Logs)
  const eventTypeMap = {
    visit: "เข้าชม (Visit)",
    register: "สมัครสมาชิก (Register)",
    add_to_cart: "เพิ่มลงตะกร้า (Add to Cart)",
    checkout: "เช็คเอาท์ (Checkout)",
    payment: "แนบสลิป/ชำระเงิน (Payment)",
    order_success: "สร้างคำสั่งซื้อ (Order Success)",
    order_approved: "อนุมัติคำสั่งซื้อ (Approved)",
    order_cancelled: "ยกเลิกคำสั่งซื้อ (Cancelled)",
  };

  const logsHeaders = [
    "ลำดับ",
    "วันที่-เวลา",
    "ประเภท Event",
    "ชื่อผู้ใช้ / ผู้ซื้อ",
    "เบอร์โทรศัพท์",
    "User ID",
    "Order ID",
    "Order Short ID",
    "ยอดเงิน (บาท)",
    "จำนวนสลาก (ใบ)",
    "Session ID",
    "งวด (Round Date)",
  ];

  const logsRows = latestEvents.map((evt, idx) => [
    idx + 1,
    evt.createdAt ? new Date(evt.createdAt).toLocaleString("th-TH") : "-",
    eventTypeMap[evt.type] || evt.type || "-",
    evt.customerName || "-",
    evt.customerPhone || "-",
    evt.userId || "-",
    evt.orderId || "-",
    evt.orderShortId ? `#${evt.orderShortId}` : "-",
    evt.orderAmount || 0,
    evt.orderSize || 0,
    evt.sessionId || "-",
    evt.roundDate || "-",
  ]);

  const wsLogs = xlsx.utils.aoa_to_sheet([logsHeaders, ...logsRows]);
  wsLogs["!cols"] = [
    { wch: 8 },
    { wch: 20 },
    { wch: 28 },
    { wch: 24 },
    { wch: 16 },
    { wch: 28 },
    { wch: 28 },
    { wch: 16 },
    { wch: 16 },
    { wch: 16 },
    { wch: 32 },
    { wch: 16 },
  ];
  xlsx.utils.book_append_sheet(wb, wsLogs, "ประวัติกิจกรรม 100 ล่าสุด");

  const roundSuffix = selectedRound ? `_งวด_${selectedRound}` : "_ทั้งหมด";
  const safeName = (linkInfo?.name || code).replace(/[\\/?%*:|"<>]/g, "_");
  const filename = `รายงาน_TrackingLink_${safeName}_${code}${roundSuffix}_ออก_${thaiDate()}.xlsx`;

  logReportDownload();
  xlsx.writeFile(wb, filename);
  return filename;
};

/**
 * ดาวน์โหลดรายงาน Excel (.xlsx) สำหรับภาพรวมของ Tracking Links ทั้งหมดในระบบ
 */
export const exportAllTrackingLinksToXLSX = ({
  trackingLinks = [],
  selectedRound,
}) => {
  const wb = xlsx.utils.book_new();

  const headers = [
    "ลำดับ",
    "ชื่อแคมเปญ / ลิงก์ติดตาม",
    "Tracking Code",
    "ผู้ติดต่อ / อ้างอิง",
    "หมายเหตุ",
    "Visits (เข้าชม)",
    "สมัครสมาชิก (คน)",
    "สร้างคำสั่งซื้อ (ออเดอร์)",
    "อนุมัติแล้ว (ออเดอร์)",
    "ยกเลิก (ออเดอร์)",
    "ยอดขายสุทธิ (บาท)",
    "สถานะ",
  ];

  const rows = trackingLinks.map((item, idx) => [
    idx + 1,
    item.name || "-",
    item.code || "-",
    item.contact || "-",
    item.note || "-",
    item.visits || 0,
    item.registers || 0,
    item.orderSuccesses || 0,
    item.orderApproved || 0,
    item.orderCancelled || 0,
    item.totalRevenue || 0,
    item.status === "active" ? "เปิดใช้งาน" : "ปิดใช้งาน",
  ]);

  const ws = xlsx.utils.aoa_to_sheet([headers, ...rows]);
  ws["!cols"] = [
    { wch: 8 },
    { wch: 30 },
    { wch: 18 },
    { wch: 20 },
    { wch: 24 },
    { wch: 16 },
    { wch: 16 },
    { wch: 22 },
    { wch: 18 },
    { wch: 16 },
    { wch: 18 },
    { wch: 14 },
  ];
  xlsx.utils.book_append_sheet(wb, ws, "ภาพรวม Tracking Links");

  const roundSuffix = selectedRound ? `_งวด_${selectedRound}` : "_ทั้งหมด";
  const filename = `รายงาน_ภาพรวม_TrackingLinks${roundSuffix}_ออก_${thaiDate()}.xlsx`;

  logReportDownload();
  xlsx.writeFile(wb, filename);
  return filename;
};
